import { observable, action } from 'mobx'
import i18next from 'i18next'
import { fetchCategories, createCategory, updateCategory, deleteCategory, deleteProtocol, archiveProtocol, restoreProtocol, downloadProtocolFile, openProtocolFile, fetchUserList, fetchUserNames, fetchMainManagers, patchMainManagerWithEmail, fetchArchiveList, fetchProtocols, fetchProtocolCountsByStatus, fetchProtocolForView, fetchProtocolForEdit, fetchProtocolForEditStatus, fetchProtocolStatusHistory, updateProtocol, updateProtocolStatus, publishProtocol, createProtocol, uploadProtocolFiles, deleteProtocolFiles, fetchUsers, fetchUserPermissions, fetchTermsOfUse, acceptTermsOfUse, Category, UserOption, UserPermissions, TermsOfUse, MainManagerEntry, MainManagerUser, ArchiveItem, ProtocolItem, ProtocolViewData, ProtocolEditData, ProtocolStatusEditData, ProtocolCountsByStatus, ProtocolStatusHistoryItem, ProtocolsListBody, CreateProtocolBody, UpdateProtocolBody, UpdateProtocolStatusBody, FileToUpload } from 'pages/Home/Protocols/api'
import { ModuleEnum } from 'utils/types'
import modulesStore from './modulesStore.store'
import snackBar from './snackbars.store'
import orgs from 'store/orgs.store'

class ProtocolsStore {
  @observable baseUrl = ''
  @observable authToken = ''
  @observable moduleId: number | null = null
  @observable communityId: number | null = null
  @observable categories: Category[] = []
  @observable categoryNames: Category[] = []
  @observable loadingCategories = false
  @observable userList: UserOption[] = []
  @observable protocols: ProtocolItem[] = []
  @observable protocolsTotalCount = 0
  @observable loadingProtocols = false
  // False until the first list response lands, so the screen can tell "still
  // loading" apart from "loaded, and there is nothing to show".
  @observable protocolsLoaded = false
  @observable protocolView: ProtocolViewData | null = null
  @observable loadingProtocolView = false
  @observable protocolStatusHistory: ProtocolStatusHistoryItem[] = []
  @observable protocolStatusHistoryTotalCount = 0
  @observable loadingProtocolStatusHistory = false
  @observable protocolEdit: ProtocolEditData | null = null
  @observable loadingProtocolEdit = false
  @observable protocolStatusEdit: ProtocolStatusEditData | null = null
  @observable loadingProtocolStatusEdit = false
  @observable protocolCounts: ProtocolCountsByStatus = { totalCount: 0, draftCount: 0, pendingApprovalCount: 0, approvedCount: 0, rejectedCount: 0 }
  @observable archiveItems: ArchiveItem[] = []
  @observable archiveTotalCount = 0
  @observable loadingArchive = false
  @observable users: UserOption[] = []
  @observable userPermissions: UserPermissions | null = null
  @observable permissionsLoaded = false
  @observable termsOfUseAccepted = true
  @observable termsOfUseLoaded = false

  @action init = async (communityId: number) => {
    this.moduleId = null
    this.communityId = orgs.currentOrgId
    this.baseUrl = ''
    this.authToken = ''
    //switching community reloads everything: the previous community's list must
    //not read as "loaded and empty" while the new one is still being fetched
    this.protocolsLoaded = false

    const protocols = modulesStore.communityModules.filter(i => i.type === ModuleEnum.PROTOCOLS)
    if (protocols.length > 0) {
      const res = await modulesStore.openProtocolModule(protocols[0].module_id, communityId)
      this.moduleId = protocols[0].module_id
      this.communityId = communityId
      this.baseUrl = (typeof protocols[0].external_url === 'string')? protocols[0].external_url.trim() : '' 
      this.authToken = res
      return true
    }

    return false
  }

  @action private refreshAuthToken = async () => {
    if (this.moduleId && this.communityId) {
      this.authToken = await modulesStore.openProtocolModule(this.moduleId, this.communityId)
    }
  }

  private withTokenRefresh = async <T>(fn: () => Promise<T>): Promise<T | null> => {
    const res = await fn()

    if ((res as any)?.unauthorized) {
      await this.refreshAuthToken()
      const retryRes = await fn()
      return (retryRes as any)?.unauthorized ? null : retryRes
    }

    return res
  }

  @action loadCategories = async (communityId: number) => {
    this.loadingCategories = true
    const data = await this.withTokenRefresh(() => fetchCategories(this.baseUrl, this.authToken, 'api/categories'))
    this.categories = (data as any) || []
    this.loadingCategories = false
  }

  @action loadCategoryNames = async () => {
    this.loadingCategories = true
    const data = await this.withTokenRefresh(() => fetchCategories(this.baseUrl, this.authToken, 'api/categories/names'))
    // /api/categories/names may return either the same Category[] shape
    // that /api/categories returns, or a lighter shape (array of strings,
    // {id,name} objects, or wrapped in { categories: [...] }). Normalise
    // defensively so `this.categoryNames` is always ProtocolItem-compatible
    // and downstream `.map(c => c.categoryName)` never crashes.
    // NOTE: this writes to `categoryNames`, not `categories` — `categories`
    // holds the full Category objects (with categoryManagers/approvingManagers)
    // that CategoryModal/Protocols.tsx rely on, and must not be clobbered by
    // this lightweight lookup that ProtocolEditModal/ProtocolModal use for
    // their category <select>.
    const raw: any[] = Array.isArray(data)
      ? data
      : Array.isArray((data as any)?.categories)
        ? (data as any).categories
        : Array.isArray((data as any)?.names)
          ? (data as any).names
          : []
    this.categoryNames = raw.map((item: any) => {
      if (item == null) return { id: '', categoryName: '' } as any
      if (typeof item === 'string' || typeof item === 'number') {
        return { id: item, categoryName: String(item) } as any
      }
      return {
        ...item,
        id: item.id ?? item.categoryId ?? item.name,
        categoryName: item.categoryName ?? item.name ?? item.title ?? '',
      }
    })
    this.loadingCategories = false
  }

  @action createCategory = async (communityId: number, body: object) => {
    const res = await this.withTokenRefresh(() => createCategory(this.baseUrl, this.authToken, body))
    // A rejected save comes back carrying `error`, which is truthy - only the
    // absence of it means the category was really created.
    if (res && !(res as any).error) {
      snackBar.showSuccess(i18next.t('protocols:category_created'))
      await this.loadCategories(communityId)
    }
    return res
  }

  @action updateCategory = async (communityId: number, categoryId: number, body: object) => {
    const res = await this.withTokenRefresh(() => updateCategory(this.baseUrl, this.authToken, categoryId, body))
    if (res && !(res as any).error) {
      snackBar.showSuccess(i18next.t('protocols:category_updated'))
      await this.loadCategories(communityId)
    }
    return res
  }

  @action deleteCategory = async (communityId: number, categoryId: number) => {
    const res = await this.withTokenRefresh(() => deleteCategory(this.baseUrl, this.authToken, categoryId))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:category_deleted'))
      await this.loadCategories(communityId)
    }
    return res
  }

  @action deleteProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => deleteProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:protocol_deleted'))
    }
    return res
  }

  @action archiveProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => archiveProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:protocol_archived'))
    }
    return res
  }

  @action restoreProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => restoreProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:protocol_restored'))
    }
    return res
  }

  @action downloadProtocolFile = async (protocolId: number, fileId: number, fileName: string) => {
    return downloadProtocolFile(this.baseUrl, this.authToken, protocolId, fileId, fileName)
  }

  @action openProtocolFile = async (protocolId: number, fileId: number, fileName: string) => {
    return openProtocolFile(this.baseUrl, this.authToken, protocolId, fileId, fileName)
  }

  @action createProtocol = async (body: CreateProtocolBody) => {
    return this.withTokenRefresh(() => createProtocol(this.baseUrl, this.authToken, body))
  }

  @action uploadProtocolFiles = async (protocolId: number, files: FileToUpload[]) => {
    return this.withTokenRefresh(() => uploadProtocolFiles(this.baseUrl, this.authToken, protocolId, files))
  }

  @action deleteProtocolFiles = async (protocolId: number, fileIds: number[], filesToUpload: FileToUpload[] = []) => {
    return this.withTokenRefresh(() => deleteProtocolFiles(this.baseUrl, this.authToken, protocolId, fileIds, filesToUpload))
  }

  @action loadUserList = async () => {
    const data = await this.withTokenRefresh(() => fetchUserList(this.baseUrl, this.authToken))
    this.userList = (data as any) || []
  }

  @action loadUserNames = async (): Promise<UserOption[]> => {
    const data = await this.withTokenRefresh(() => fetchUserNames(this.baseUrl, this.authToken))
    return (data as any) || []
  }

  @action loadMainManagers = async (): Promise<MainManagerUser[]> => {
    const data = await this.withTokenRefresh(() => fetchMainManagers(this.baseUrl, this.authToken))
    return (data as any) || []
  }

  @action saveMainManagers = async (entries: MainManagerEntry[]): Promise<boolean> => {
    const res = await this.withTokenRefresh(() => patchMainManagerWithEmail(this.baseUrl, this.authToken, entries))
    return res !== null
  }

  // Monotonic id so overlapping list requests (rapid search/filter/page
  // changes) can't clobber each other — only the newest response is applied.
  loadProtocolsSeq = 0
  @action loadProtocols = async (body: ProtocolsListBody) => {
    const seq = ++this.loadProtocolsSeq
    this.loadingProtocols = true
    const data = await this.withTokenRefresh(() => fetchProtocols(this.baseUrl, this.authToken, body))
    if (seq !== this.loadProtocolsSeq) return // superseded by a newer request
    if (Array.isArray(data)) {
      this.protocols = data
      this.protocolsTotalCount = data.length
    } else {
      this.protocols = (data as any)?.protocols || []
      this.protocolsTotalCount = (data as any)?.totalCount || 0
    }
    this.loadingProtocols = false
    this.protocolsLoaded = true
  }

  @action markProtocolAsRead = (protocolId: number) => {
    const target = this.protocols.find(p => p.id === protocolId)
    if (target && !target.isRead) {
      target.isRead = true
    }
  }

  @action loadProtocolForView = async (protocolId: number) => {
    this.loadingProtocolView = true
    this.protocolView = null
    const data = await this.withTokenRefresh(() => fetchProtocolForView(this.baseUrl, this.authToken, protocolId))
    this.protocolView = (data as ProtocolViewData) || null
    this.loadingProtocolView = false
  }

  // Monotonic id so overlapping status-history requests (rapid paging / date
  // filtering) can't clobber each other — only the newest response is applied.
  loadStatusHistorySeq = 0
  @action loadProtocolStatusHistory = async (protocolId: number, pageNumber: number, pageSize: number, fromDate?: string, toDate?: string) => {
    const seq = ++this.loadStatusHistorySeq
    this.loadingProtocolStatusHistory = true
    try {
      const data = await this.withTokenRefresh(() => fetchProtocolStatusHistory(this.baseUrl, this.authToken, protocolId, {
        pageNumber, pageSize, ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}),
      }))
      if (seq !== this.loadStatusHistorySeq) return // superseded by a newer request
      this.protocolStatusHistory = (data as any)?.items || []
      this.protocolStatusHistoryTotalCount = (data as any)?.totalCount || 0
    } finally {
      // Only the newest request clears the flag (a superseded one leaves it to the newer).
      if (seq === this.loadStatusHistorySeq) this.loadingProtocolStatusHistory = false
    }
  }

  @action loadProtocolForEdit = async (protocolId: number) => {
    this.loadingProtocolEdit = true
    this.protocolEdit = null
    const data = await this.withTokenRefresh(() => fetchProtocolForEdit(this.baseUrl, this.authToken, protocolId))
    this.protocolEdit = (data as ProtocolEditData) || null
    this.loadingProtocolEdit = false
  }

  // Lightweight load for the Approve/Reject screen - kept apart from protocolEdit (full edit screen).
  @action loadProtocolForEditStatus = async (protocolId: number) => {
    this.loadingProtocolStatusEdit = true
    this.protocolStatusEdit = null
    const data = await this.withTokenRefresh(() => fetchProtocolForEditStatus(this.baseUrl, this.authToken, protocolId))
    this.protocolStatusEdit = (data as ProtocolStatusEditData) || null
    this.loadingProtocolStatusEdit = false
  }

  @action updateProtocol = async (protocolId: number, body: UpdateProtocolBody) => {
    const res = await this.withTokenRefresh(() => updateProtocol(this.baseUrl, this.authToken, protocolId, body))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:protocol_updated'))
    }
    return res
  }

  @action updateProtocolStatus = async (protocolId: number, body: UpdateProtocolStatusBody) => {
    return this.withTokenRefresh(() => updateProtocolStatus(this.baseUrl, this.authToken, protocolId, body))
  }

  @action publishProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => publishProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess(i18next.t('protocols:protocol_published'))
    }
    return res
  }

  @action loadProtocolCounts = async () => {
    const data = await this.withTokenRefresh(() => fetchProtocolCountsByStatus(this.baseUrl, this.authToken))
    if (data) this.protocolCounts = data as ProtocolCountsByStatus
  }

  @action loadArchive = async (body: ProtocolsListBody) => {
    this.loadingArchive = true
    const data = await this.withTokenRefresh(() => fetchArchiveList(this.baseUrl, this.authToken, body))
    this.archiveItems = (data as any)?.protocols || []
    this.archiveTotalCount = (data as any)?.totalCount || 0
    this.loadingArchive = false
  }

  @action loadUsers = async () => {
    const data = await this.withTokenRefresh(() => fetchUsers(this.baseUrl, this.authToken))
    this.users = (data as any) || []
  }

  @action loadUserPermissions = async () => {
    this.permissionsLoaded = false
    const data = await this.withTokenRefresh(() => fetchUserPermissions(this.baseUrl, this.authToken))
    this.userPermissions = (data as UserPermissions) || null
    this.permissionsLoaded = true
    return true
  }

  @action loadTermsOfUse = async () => {
    this.termsOfUseLoaded = false
    const data = await this.withTokenRefresh(() => fetchTermsOfUse(this.baseUrl, this.authToken))
    this.termsOfUseAccepted = (data as TermsOfUse)?.isAcceptedTerms ?? true
    this.termsOfUseLoaded = true
    return true
  }

  @action acceptTermsOfUse = async () => {
    const res = await this.withTokenRefresh(() => acceptTermsOfUse(this.baseUrl, this.authToken))
    if (res) {
      this.termsOfUseAccepted = true
    }
    return res
  }
}

const protocolsStore = new ProtocolsStore()
export default protocolsStore
